import { useApolloClient } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Divider,
  EmptyStation,
  formatDateTime,
  FormStation,
  getFormDiff,
  InfoPanel,
  MessageBar,
  Paragraph,
  RadioField,
  Section,
} from '@axinom/mosaic-ui';
import { Field, type FormikValues, useFormikContext } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  IdentityProvider,
  type IdpConfiguration,
  useIdpConfigurationsQuery,
  useUpdateIdpConfigurationMutation,
} from '../../../../generated/id-service-env-administration';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';

export const IdpAxinomDetail: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
  }>();

  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);
  const apolloClient = useApolloClient();

  // Check the status of id-service
  useEffect(() => {
    (async () => {
      const statusMessage = await getManagedServiceAvailabilityMessage(
        ManagedServiceId.ID_SERVICE,
        envId,
      );
      setServiceStatusMessage(statusMessage);
    })();
  }, [envId]);

  const { loading, data, error } = useIdpConfigurationsQuery({
    client: apolloClient,
    variables: {
      idpConfigurationCondition: {
        environmentId: envId,
        idpId: IdentityProvider.Axinom,
      },
      idpScopeFilter: {
        idpId: {
          equalTo: IdentityProvider.Axinom,
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  const [updateIdpConfig] = useUpdateIdpConfigurationMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const initialData = data?.configurations?.nodes[0];
  const detailObj = initialData ?? {
    environmentId: envId,
    enabled: false,
  };

  const onSubmit = useCallback(
    async (formData: FormikValues, initialData): Promise<void> => {
      if (initialData.loading) {
        return;
      }

      if (initialData.data?.id) {
        await updateIdpConfig({
          variables: {
            input: {
              id: initialData.data?.id,
              patch: getFormDiff(formData, initialData.data),
            },
          },
        });
      }
    },
    [updateIdpConfig],
  );

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="Axinom Identity Provider" subtitle="Configuration">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <FormStation
          defaultTitle="Axinom Identity Provider"
          subtitle="Configuration"
          initialData={{
            data: detailObj,
            loading,
            error: error?.message,
          }}
          saveData={onSubmit}
          infoPanel={initialData ? <Panel /> : undefined}
          // TODO: This is until we have a proper solution for it (TASK ID: 45803)
          stationMessage={{
            title: `This Identity Provider is currently available only for non-local development (i.e. when not running the solution on 'localhost').`,
            type: 'info',
          }}
        >
          <Form />
        </FormStation>
      )}
    </>
  );
};

const Panel: React.FC = () => {
  const { values } = useFormikContext<IdpConfiguration>();

  return (
    <InfoPanel>
      <Section>
        <Paragraph title="Last Modified">
          {formatDateTime(values.updatedAt)} by {values.updatedBy}
        </Paragraph>
      </Section>
      <Section>
        <Paragraph title="Tenant ID">{values.tenantId}</Paragraph>
        <Paragraph title="Environment ID">{values.environmentId}</Paragraph>
      </Section>
    </InfoPanel>
  );
};

const Form: React.FC = () => {
  return (
    <>
      <Field
        name="enabled"
        label="IDP Status"
        options={[
          { value: true, label: 'Enabled' },
          { value: false, label: 'Disabled' },
        ]}
        transform={(value: string) => JSON.parse(value)}
        as={RadioField}
      />
      <Divider />
    </>
  );
};

export const AxinomProviderDetailCrumb: BreadcrumbResolver = () => 'Axinom IDP';
